import {take, call, put, cancelled, takeLatest} from 'redux-saga/effects'
import {userActions, dataActions, alertActions} from '../actions'
import {userConstants, alertConstants} from '../constants'
import AuthService from '../utils/AuthService'
import {getDataAPI} from '../utils/DataService'
import base64url from 'base64url'
import {isEmpty} from 'ramda'

/******************************************************************************/
/************************* URLs to the API services ***************************/
/******************************************************************************/
const authService = new AuthService(process.env.REACT_APP_API_URL)

/******************************************************************************/
/******************************* WATCHERS *************************************/
/******************************************************************************/
export function* watchLogin() {
    yield takeLatest(userConstants.LOGIN_REQUEST, loginSaga)
}

export function* watchRefreshToken() {
    yield takeLatest(userConstants.LOGIN_REFRESH, refreshtokenSaga)
}

export function* watchLogout() {
    while (true) {
        const {message} = yield take(userConstants.LOGOUT)

        yield call(logoutSaga, message)
    }
}

export function* watchisLoggedIn() {
    yield takeLatest(userConstants.LOGIN_CHECK, loginCheckSaga)
}

export function* watchgetUserRolesSaga() {
    yield takeLatest(
        userConstants.LOGIN_GETUSERPROFILE_REQUEST,
        getuserprofileSaga
    )
}

/******************************************************************************/
/********************************* Sagas **************************************/
/******************************************************************************/
function* loginSaga(action) {
    let data
    let token

    try {
        data = yield call(
            authService.login,
            action.credentials.username,
            action.credentials.password
        )

        if (data) {
            token = data['token']

            const tokenSegments = token.split('.')
            const body = JSON.parse(base64url.decode(tokenSegments[1]))
            const displayname = body['firstName'] + ' ' + body['lastName']
            const firstname = body['firstName']
            const lastname = body['lastName']
            const euid = body['euid']
            const emplid = body['emplid']
            const exp = body['exp']

            yield put(
                userActions.loginSuccess(
                    token,
                    displayname,
                    firstname,
                    lastname,
                    euid,
                    emplid,
                    exp
                )
            )
            yield authService.setAccessToken(token)
            yield put(
                alertActions.success(
                    'Welcome ' + firstname + '. You have logged in successfully'
                )
            )
            // Get logged in user profile
            yield put(userActions.getUserProfileRequest(token))
            // return the token for health and wealth
            return token
        }
    } catch (e) {
        const errorMessage =
            (e.title ? e.title : 'Login error') + ': ' + e.message

        yield put(userActions.loginFailure(errorMessage))

        yield put(alertActions.error(errorMessage))

        return
    } finally {
        if (yield cancelled()) {
            yield authService.logout()
            yield put(userActions.logout())
        }
    }
}

function* logoutSaga(message) {
    try {
        yield authService.logout()
        yield put(alertActions.warning(message))
        return
    } catch (e) {
        yield put(userActions.loginFailure(e))
        yield put(alertActions.error(e))
        return
    }
}

function* loginCheckSaga(action) {
    let validtoken
    const loggedin = action.loggedin

    try {
        validtoken = yield authService.isLoggedIn()

        if (!validtoken) {
            if (loggedin) {
                yield put(
                    userActions.logout(
                        'Logged out because your token has expired'
                    )
                )
            }
            return
        } else {
            yield put(userActions.isLoggedIn())

            return
        }
    } catch (e) {
        yield put(userActions.loginFailure(e))
        yield put(alertActions.error(e))
        return
    }
}

function* refreshtokenSaga(action) {
    let data
    let token
    let errormsg
    const {noalertmsg} = action

    try {
        data = yield call(authService.refreshtoken, action.token)

        if (data) {
            token = data['token']

            const tokenSegments = token.split('.')
            const body = JSON.parse(base64url.decode(tokenSegments[1]))
            const displayname = body['firstName'] + ' ' + body['lastName']
            const firstname = body['firstName']
            const lastname = body['lastName']
            const euid = body['euid']
            const emplid = body['emplid']
            const exp = body['exp']

            yield put(
                userActions.loginSuccess(
                    token,
                    displayname,
                    firstname,
                    lastname,
                    euid,
                    emplid,
                    exp
                )
            )
            yield authService.setAccessToken(token)
            if (!noalertmsg) {
                yield put(
                    alertActions.success(
                        'Well done ' +
                            firstname +
                            '! You have renewed your login session successfully'
                    )
                )
            }
            // return the token for health and wealth
            return token
        } else {
            if (data['title']) {
                errormsg = data['title'] + ': ' + data['message']
            } else {
                errormsg = 'Login error: ' + data['message']
            }
            yield put(userActions.loginFailure(errormsg))
            yield put(alertActions.error(errormsg))
        }
    } catch (e) {
        const errorMessage =
            (e.title ? e.title : 'Login error') + ': ' + e.message

        yield put(userActions.loginFailure(errorMessage))

        yield put(alertActions.error(errorMessage))

        return
    } finally {
        if (yield cancelled()) {
            yield authService.logout()
            yield put(userActions.logout())
        }
    }
}

function* getuserprofileSaga(action) {
    const abortController = new AbortController()
    let userprofile

    const {token} = action

    try {
        userprofile = yield call(
            getDataAPI,
            '/users/self',
            token,
            userConstants.LOGIN_GETUSERPROFILE_SUCCESS,
            userConstants.LOGIN_GETUSERPROFILE_FAILURE,
            alertConstants.ERROR
        )

        if (
            userprofile.admin ||
            (!isEmpty(userprofile.roles) &&
                ('creator' in userprofile.roles ||
                    'admin' in userprofile.roles))
        )
            yield put(dataActions.departmentsDataRequest(token))
    } catch (err) {
        const errorMessage = err.title + ': ' + err.message

        if (yield cancelled()) {
            //Cancel the API call if the saga was cancelled
            abortController.abort()
        }

        yield put({
            type: userConstants.LOGIN_GETUSERPROFILE_FAILURE,
            message: errorMessage,
        })
        yield put({type: alertConstants.ERROR, message: errorMessage})
    }

    return userprofile
}
